import { useEffect, useRef, type InputHTMLAttributes, type ReactNode } from "react";
import "./Checkbox.css";

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "children"> {
  /** Rendered beside the box. Omit for a bare checkbox in a table cell or similar. */
  label?: ReactNode;
  /**
   * Partially-checked state, for a "select all" that covers some of its group. Cannot be expressed
   * in JSX — it is a DOM property, not an attribute — so it is applied through a ref.
   */
  indeterminate?: boolean;
}

/**
 * The project's checkbox.
 *
 * A real `<input type="checkbox">` is kept in the DOM and only visually replaced: it stays
 * focusable, keyboard-operable, announced correctly, and reachable by `form` and label semantics.
 * Styling the box with `appearance: none` on the input itself would work for the box but loses the
 * check mark, so the mark is a sibling span the CSS reveals.
 *
 * Prefer this over a native checkbox anywhere in the app. See "Design system" in web/CLAUDE.md.
 */
export const Checkbox = ({
  label,
  indeterminate = false,
  className = "",
  disabled,
  ...props
}: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <label
      className={`checkbox${disabled ? " checkbox--disabled" : ""} ${className}`.trim()}
    >
      <span className="checkbox__control">
        <input
          ref={inputRef}
          type="checkbox"
          className="checkbox__input"
          disabled={disabled}
          {...props}
        />
        {/*
          aria-hidden: the input already carries the state. Announcing the mark as well would have
          a screen reader read the control twice.
        */}
        <span className="checkbox__box" aria-hidden="true">
          <svg
            className="checkbox__check"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 6.2L4.8 8.5L9.5 3.5"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="checkbox__dash" />
        </span>
      </span>
      {label !== undefined && <span className="checkbox__label">{label}</span>}
    </label>
  );
};
